import { useEffect, useRef, useState } from "react";

/**
 * 자율주행차 카메라 4분할 뷰 (하단 밴드, ADMIN 전용).
 *
 * 프로토콜: 인제스트 = WebRTC(WHIP, 추후 SRT) / 송출 = WebRTC(WHEP).
 *   브라우저는 SRT를 직접 재생할 수 없어 MediaMTX가 SRT/WHIP → WHEP 로 변환해 준다.
 *   → SRT 전환 시 이 컴포넌트는 무변경(WHEP 그대로).
 *
 * 스트림 2개: 전방 합성(3폭) + 후방. 전방 합성 1스트림을 3개의 <video>가 공유하며
 *   각각 좌/중/우 1/3 만 CSS crop 으로 노출한다.
 *   → 추후 카메라별 개별 스트리밍 전환 = 아래 TILES 배열만 교체(stream 이름 4개 + crop 제거).
 *
 * 뷰별 토글: 어떤 스트림을 참조하는 타일이 전부 꺼지면 그 WHEP 연결을 닫아 대역폭을 절약한다
 *   (전방 3타일은 1스트림을 공유하므로 3개 모두 꺼야 전방 스트림이 닫힘. 후방은 독립).
 */

type StreamName = "front" | "rear";
type Tile = { label: string; stream: StreamName; cropIndex?: number; cols?: number };

const TILES: Tile[] = [
    { label: "전방 좌", stream: "front", cropIndex: 0, cols: 3 },
    { label: "전방 중", stream: "front", cropIndex: 1, cols: 3 },
    { label: "전방 우", stream: "front", cropIndex: 2, cols: 3 },
    { label: "후방", stream: "rear" },
];

// MediaMTX(WHEP) 베이스 URL. 로컬 개발은 PoC MediaMTX(:8889).
// TODO(운영): 실제 미디어 호스트(예: media.ezmobility.co.kr) + https 로 확정.
function mediaBase(): string {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1") return "http://localhost:8889";
    return `${window.location.protocol}//${h}:8889`;
}

// 프로덕션에서는 여기서 GET /v1/apollo/{carId}/watch-token 으로 짧은 수명 토큰을 받아 Authorization 에 부착.
async function fetchWatchToken(_carId: number, _stream: StreamName): Promise<string | undefined> {
    return undefined; // 로컬 PoC(MediaMTX 무인증)는 토큰 없이 연결
}

// 하나의 WHEP 스트림 연결 → MediaStream 반환
async function connectWhep(url: string, token?: string): Promise<{ pc: RTCPeerConnection; stream: MediaStream }> {
    const pc = new RTCPeerConnection();
    const stream = new MediaStream();
    pc.addTransceiver("video", { direction: "recvonly" });
    pc.ontrack = (e) => {
        e.streams[0]?.getTracks().forEach((t) => stream.addTrack(t));
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // ICE 수집 완료까지 대기(non-trickle WHEP). 안전장치로 최대 2초.
    await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === "complete") return resolve();
        const check = () => {
            if (pc.iceGatheringState === "complete") {
                pc.removeEventListener("icegatheringstatechange", check);
                resolve();
            }
        };
        pc.addEventListener("icegatheringstatechange", check);
        setTimeout(resolve, 2000);
    });

    const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/sdp", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: pc.localDescription?.sdp ?? "",
    });
    if (!res.ok) {
        pc.close();
        throw new Error(`WHEP ${res.status}`);
    }
    const answer = await res.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answer });
    return { pc, stream };
}

export default function CameraGrid({ carId }: { carId: number | null }) {
    const [visible, setVisible] = useState<boolean[]>(TILES.map(() => true));
    const [status, setStatus] = useState<Record<string, string>>({});
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const conns = useRef<Record<string, { pc: RTCPeerConnection; stream: MediaStream }>>({});

    // 켜진 타일이 필요로 하는 스트림 집합(정렬 key 로 의존성 안정화)
    const neededKey = Array.from(new Set(TILES.filter((_, i) => visible[i]).map((t) => t.stream)))
        .sort()
        .join(",");

    const attachStreamToTiles = (name: string, ms: MediaStream | null) => {
        TILES.forEach((t, i) => {
            if (t.stream === name && videoRefs.current[i]) videoRefs.current[i]!.srcObject = ms;
        });
    };

    useEffect(() => {
        const closeAll = () => {
            Object.values(conns.current).forEach((c) => c.pc.close());
            conns.current = {};
        };
        if (carId == null) {
            closeAll();
            setStatus({});
            return;
        }

        const want = new Set(neededKey ? neededKey.split(",") : []);
        let cancelled = false;

        // 1) 필요 없어진 스트림 닫기(대역폭 절약)
        Object.keys(conns.current).forEach((name) => {
            if (!want.has(name)) {
                conns.current[name].pc.close();
                delete conns.current[name];
                attachStreamToTiles(name, null);
                setStatus((s) => ({ ...s, [name]: "off" }));
            }
        });

        // 2) 필요한데 없는 스트림 열기
        want.forEach(async (name) => {
            if (conns.current[name]) return;
            setStatus((s) => ({ ...s, [name]: "connecting" }));
            try {
                const token = await fetchWatchToken(carId, name as StreamName);
                const url = `${mediaBase()}/apollo_${carId}_${name}/whep`;
                const c = await connectWhep(url, token);
                if (cancelled || conns.current[name]) {
                    c.pc.close();
                    return;
                }
                conns.current[name] = c;
                c.pc.oniceconnectionstatechange = () =>
                    setStatus((s) => ({ ...s, [name]: c.pc.iceConnectionState }));
                attachStreamToTiles(name, c.stream);
                setStatus((s) => ({ ...s, [name]: "live" }));
            } catch (e) {
                if (!cancelled) setStatus((s) => ({ ...s, [name]: "연결 실패" }));
            }
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carId, neededKey]);

    // 언마운트 시 전체 연결 정리
    useEffect(
        () => () => {
            Object.values(conns.current).forEach((c) => c.pc.close());
            conns.current = {};
        },
        []
    );

    const toggle = (i: number) => setVisible((v) => v.map((x, j) => (j === i ? !x : x)));

    return (
        <div style={{ display: "flex", gap: 8, height: "100%" }}>
            {TILES.map((t, i) => {
                const on = visible[i];
                const st = status[t.stream];
                return (
                    <div
                        key={i}
                        style={{
                            position: "relative",
                            flex: t.stream === "rear" ? "0 0 22%" : 1,
                            overflow: "hidden",
                            background: "#05080d",
                            border: "1px solid #1e2836",
                            borderRadius: 6,
                        }}
                    >
                        <video
                            ref={(el) => {
                                videoRefs.current[i] = el;
                            }}
                            autoPlay
                            muted
                            playsInline
                            style={
                                t.cropIndex != null
                                    ? { position: "absolute", top: 0, height: "100%", width: `${(t.cols ?? 1) * 100}%`, left: `-${t.cropIndex * 100}%` }
                                    : { width: "100%", height: "100%", objectFit: "cover" }
                            }
                        />

                        {/* 꺼짐 오버레이 */}
                        {!on && (
                            <div style={{ position: "absolute", inset: 0, background: "#05080d", display: "flex", alignItems: "center", justifyContent: "center", color: "#4a5a72", fontSize: 12 }}>
                                꺼짐
                            </div>
                        )}

                        {/* 라벨 + 토글 */}
                        <div style={{ position: "absolute", top: 4, left: 6, right: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 12, color: "#dbe4f2", background: "rgba(0,0,0,0.55)", padding: "1px 6px", borderRadius: 4 }}>{t.label}</span>
                            <button
                                onClick={() => toggle(i)}
                                style={{ fontSize: 11, lineHeight: "16px", padding: "0 6px", cursor: "pointer", borderRadius: 4, border: "1px solid #2a3546", background: on ? "#1f6feb" : "#171f2b", color: on ? "#fff" : "#9aa9c0" }}
                            >
                                {on ? "◉ ON" : "◯ OFF"}
                            </button>
                        </div>

                        {/* 연결 상태(정상 live 외에만 표시) */}
                        {on && st && st !== "live" && (
                            <span style={{ position: "absolute", bottom: 4, left: 6, fontSize: 11, color: "#ffcc66", background: "rgba(0,0,0,0.55)", padding: "1px 6px", borderRadius: 4 }}>
                                {st}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
